import { styled } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  Box,
  Button,
  LinearProgress,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "common/locales";
import { AnalyticsEvents, analytics } from "../common/analytics";

import { restoreToken } from "common/utils/token";
import { sendPasswordResetEmail } from "services";
import { useAppStore } from "stores";

import RuumiLogo from "assets/images/ruumi-logo-light.png";
import LoginCarousel from "./LoginCarousel";

interface LocationType {
  mail: string;
}

enum EMAIL_STATUS {
  DEFAULT,
  SENT_SUCCESS,
  SENT_FAILURE,
}

const DARK_GREEN = "#1C4321";
const LIGHT_GREEN = "#B5E48C";
const LINK_COLOR = "#F7FCF1";

const PageContainer = styled(Box)({
  display: "flex",
  minHeight: "100vh",
  width: "100%",
});

const CarouselSection = styled(Box)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.up("sm")]: {
    display: "block",
    flex: "0 0 60%",
    height: "100vh",
    position: "relative",
  },
}));

const FormSection = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: DARK_GREEN,
  padding: theme.spacing(4),
  minHeight: "100vh",
  position: "relative",
  [theme.breakpoints.down("sm")]: {
    paddingTop: `calc(env(safe-area-inset-top) + ${theme.spacing(4)})`,
    paddingBottom: `calc(env(safe-area-inset-bottom) + ${theme.spacing(4)})`,
  },
}));

const FormContainer = styled(Box)({
  width: "100%",
  maxWidth: "360px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const Logo = styled("img")({
  width: "120px",
  height: "auto",
  marginBottom: "32px",
});

const StyledTextField = styled(TextField)({
  width: "100%",
  marginBottom: "16px",

  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "12px",

    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: LIGHT_GREEN,
    },

    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: LIGHT_GREEN,
      borderWidth: "2px",
    },
  },

  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
  },

  "& .MuiInputLabel-root": {
    color: "#666",

    "&.Mui-focused": {
      color: DARK_GREEN,
    },

    "&.MuiInputLabel-shrink": {
      backgroundColor: "white",
      padding: "0 6px",
      marginLeft: "-4px",
      borderRadius: "12px 12px 0 0",
      border: `2px solid ${LIGHT_GREEN}`,
      borderBottom: "2px solid white",
      transform: "translate(14px, -18px) scale(0.75)",
    },
  },

  "& .MuiOutlinedInput-input": {
    padding: "16px",
  },
});

const SubmitButton = styled(Button)({
  width: "100%",
  backgroundColor: LIGHT_GREEN,
  color: DARK_GREEN,
  borderRadius: "12px",
  padding: "14px 24px",
  fontSize: "1.1rem",
  fontWeight: 600,
  textTransform: "none",
  marginTop: "8px",
  border: `2px solid ${LIGHT_GREEN}`,
  transition: "all 0.2s ease-in-out",

  "&:hover": {
    backgroundColor: DARK_GREEN,
    color: LIGHT_GREEN,
    borderColor: LIGHT_GREEN,
  },

  "&:disabled": {
    backgroundColor: "rgba(181, 228, 140, 0.5)",
    color: "rgba(28, 67, 33, 0.5)",
    borderColor: "transparent",
  },

  "& .MuiButton-endIcon": {
    marginLeft: "8px",
    color: "inherit",
  },

  "& .MuiSvgIcon-root": {
    color: "inherit",
  },
});

const LinksContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  width: "100%",
  marginTop: "24px",
});

const LinkButton = styled(Button)({
  color: LINK_COLOR,
  textTransform: "none",
  fontWeight: 400,
  padding: "8px 0",

  "&:hover": {
    backgroundColor: "transparent",
    textDecoration: "underline",
  },
});

const ProgressBar = styled(LinearProgress)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,

  "& .MuiLinearProgress-bar": {
    backgroundColor: LIGHT_GREEN,
  },

  "&.MuiLinearProgress-root": {
    backgroundColor: "rgba(181, 228, 140, 0.3)",
  },
});

const MessageText = styled(Typography)({
  color: LINK_COLOR,
  textAlign: "center",
  marginBottom: "16px",
});

export default function PasswordForgotDialog() {
  const [mail, setMail] = useState("");
  const [emailSent, setEmailSent] = useState<EMAIL_STATUS>(
    EMAIL_STATUS.DEFAULT
  );
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const loginInProgress = useAppStore((state) => state.isLoggingIn);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (location.state) {
      setMail((location.state as LocationType).mail);
    } else {
      const params = new URLSearchParams(location.search);
      if (params.has("mail")) {
        setMail(params.get("mail") as string);
      }
    }
  }, [location]);

  const { t } = useTranslation();

  restoreToken(location);

  const handleResetEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    analytics.track(AnalyticsEvents.PASSWORD_RESET_STARTED);

    try {
      const result = await sendPasswordResetEmail(mail);
      setEmailSent(EMAIL_STATUS.SENT_SUCCESS);
      if (result?.emailSent) {
        analytics.track(AnalyticsEvents.PASSWORD_RESET_EMAIL_SENT);
      } else {
        analytics.track(AnalyticsEvents.PASSWORD_RESET_EMAIL_COOLDOWN);
      }
    } catch (err) {
      console.log(err);
      setEmailSent(EMAIL_STATUS.SENT_FAILURE);
      analytics.track(AnalyticsEvents.PASSWORD_RESET_EMAIL_FAILED);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  return (
    <PageContainer>
      {!isMobile && (
        <CarouselSection>
          <LoginCarousel />
        </CarouselSection>
      )}
      <FormSection>
        {loginInProgress && <ProgressBar />}
        <FormContainer>
          <Logo src={RuumiLogo} alt="Ruumi logo" />

          {emailSent === EMAIL_STATUS.DEFAULT && (
            <form onSubmit={handleResetEmail} style={{ width: "100%" }}>
              <StyledTextField
                variant="outlined"
                required
                id="input-mail"
                disabled={loginInProgress}
                type="email"
                value={mail}
                onChange={(e) => setMail(e.target.value)}
                label={t("passwordForgot.mail.label")}
                autoFocus={true}
                data-private
              />
              <SubmitButton
                type="submit"
                variant="contained"
                disabled={loginInProgress || mail.trim().length === 0}
                endIcon={<ArrowForwardIcon />}
                disableElevation
              >
                {t("passwordForgot.button.label")}
              </SubmitButton>
            </form>
          )}

          {emailSent === EMAIL_STATUS.SENT_SUCCESS && (
            <>
              <MessageText>{t("passwordForgot.success")}</MessageText>
              <MessageText>{t("passwordForgot.success.2")}</MessageText>
            </>
          )}

          {emailSent === EMAIL_STATUS.SENT_FAILURE && (
            <MessageText>{t("passwordForgot.failure")}</MessageText>
          )}

          <LinksContainer>
            <LinkButton onClick={handleBackToLogin}>Back to Login</LinkButton>
          </LinksContainer>
        </FormContainer>
      </FormSection>
    </PageContainer>
  );
}
